import React, { useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import MaButton from "../../../../Components/UI/ma-button";
import { TfiDownload } from "react-icons/tfi";
import { MdAdd } from "react-icons/md";
import { Grid } from "@mui/material";
import { StatCard } from "../dashboard/stat-card";
import icon1 from "../../../../assets/Icons/dashboard/product.svg";
import {
  GET_ALL_ADDRESS,
  GET_CURRENT_SUBSCRIPTION_PLAN,
  GET_SELLER_PRODUCT_STATS,
  GET_SELLER_PRODUCTS,
} from "../../../../Utils/Queries";
import AddAddress from "../../../../Account-Profile/Components/Address/AddAddress";
import ProductTable from "./product-table";
import AddProductModal from "./add-product-modal";
import { RequireAddressDialog } from "./require-address-dialog";

const STAT_CARD_DEFS = [
  {
    label: "Total Products",
    field: "totalProducts",
    format: (v) => (v == null ? "—" : String(v)),
    icon: icon1,
    iconBg: "bg-[#EAF2FF]",
  },
  {
    label: "Active",
    field: "activeProducts",
    format: (v) => (v == null ? "—" : String(v)),
    icon: icon1,
    iconBg: "bg-[#E6F6EC]",
  },
  {
    label: "Low Stock",
    field: "lowStockProducts",
    format: (v) => (v == null ? "—" : String(v)),
    icon: icon1,
    iconBg: "bg-[#F3F4F6]",
  },
  {
    label: "Out of Stock",
    field: "outOfStockProducts",
    format: (v) => (v == null ? "—" : String(v)),
    icon: icon1,
    iconBg: "bg-[#FDECEC]",
  },
];

function getPlanLimit(plan) {
  if (!plan) return 10;

  const code = String(plan?.code ?? "").toLowerCase();
  const limit = Number(plan?.productLimit);

  if (code === "enterprise" || !Number.isFinite(limit) || limit <= 0) {
    return Infinity;
  }
  return limit;
}

function SubscriptionLimitBanner({
  plan,
  subscription,
  uploadedProducts,
  loading,
  error,
}) {
  const planName = loading ? "Loading plan..." : plan?.name || "Free Plan";
  const planLimit = getPlanLimit(plan);
  const isUnlimited = planLimit === Infinity;
  const safeUploadedProducts = Number(uploadedProducts) || 0;
  const remainingProducts = isUnlimited
    ? Infinity
    : Math.max(0, planLimit - safeUploadedProducts);
  const usagePercent = isUnlimited
    ? 100
    : Math.min(100, Math.round((safeUploadedProducts / planLimit) * 100));
  const isNearLimit = !isUnlimited && usagePercent >= 80 && usagePercent < 100;
  const isLimitReached = !isUnlimited && remainingProducts <= 0;
  const progressColor = isLimitReached
    ? "bg-[#DC2626]"
    : isNearLimit
      ? "bg-[#F59E0B]"
      : "bg-[#2E1834]";
  const accentGlow = isLimitReached
    ? "bg-[#FB7185]"
    : isNearLimit
      ? "bg-[#FBBF24]"
      : "bg-[#B69CFF]";
  const message = loading
    ? "Checking your plan limits and current product usage..."
    : error
    ? "We could not load your subscription details. Your product tools are still available."
    : isUnlimited
      ? "Unlimited Product Uploads Enabled"
      : isLimitReached
        ? "You have reached your upload limit. Upgrade your plan to add more products."
        : isNearLimit
          ? "You're almost at your upload limit. Upgrade your plan to upload more products."
          : `You're currently on the ${planName}. You can upload up to ${planLimit} products.`;

  return (
    <div className="relative mt-5 overflow-hidden rounded-[0px] border border-solid border-[#A8D98E] bg-[#D7F2C5] p-[1px] ">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.7),transparent_34%),linear-gradient(135deg,#CBEFB5_0%,#F0FAE5_44%,#B8E99C_100%)]" />
      <div
        className={`absolute -right-16 -top-20 h-52 w-52 rounded-none ${accentGlow} opacity-40 blur-3xl`}
      />
      <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-none bg-[#7CC75A] opacity-28 blur-3xl" />
      <div className="relative rounded-[0px] bg-[linear-gradient(135deg,rgba(255,255,255,0.9)_0%,rgba(240,250,229,0.88)_50%,rgba(255,255,255,0.94)_100%)] p-5 backdrop-blur-xl">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0 lg:max-w-[34%]">
          <p className="my-0 text-[9px] font-bold uppercase tracking-[0.12em] text-[#8A8A9A]">
            Current Plan
          </p>
          <h2 className="my-0 mt-2 text-[20px] font-bold text-[#272727]">
            {planName}
          </h2>
          
          <p className="my-0 mt-1 text-[12px] leading-relaxed text-[#626262]">
            {message}
          </p>
        </div>

        <div className="min-w-0 flex-1 rounded-[0px] border border-solid border-white/70 bg-white/70 p-4">
          <div className="mb-3 flex items-center justify-between gap-4">
            <p className="my-0 text-[13px] font-semibold text-[#3A3A3A]">
              Product usage
            </p>
            <p className="my-0 text-[12px] font-bold text-[#272727]">
              {isUnlimited
                ? `${safeUploadedProducts} Products`
                : `${safeUploadedProducts} / ${planLimit} Products Used`}
            </p>
          </div>
          <div className="h-[5px] overflow-hidden rounded-full bg-[#E8E8EF]">
            <div
              className={`h-full rounded-full transition-all duration-500 ${progressColor}`}
              style={{ width: `${usagePercent}%` }}
            />
          </div>
          <div className="mt-3 flex flex-col gap-1 text-[10px] text-[#74767B] sm:flex-row sm:items-center sm:justify-between">
            <span>
              {isUnlimited
                ? "No upload ceiling on this plan"
                : `${remainingProducts} uploads remaining`}
            </span>
            {!isUnlimited ? <span>{usagePercent}% used</span> : null}
          </div>
        </div>

       
        </div>
      </div>
    </div>
  );
}

const SellerProducts = () => {
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [requireAddressOpen, setRequireAddressOpen] = useState(false);
  const [addAddressOpen, setAddAddressOpen] = useState(false);
  const client = useApolloClient();
  const { data, loading } = useQuery(GET_SELLER_PRODUCT_STATS);
  const {
    data: subscriptionData,
    loading: subscriptionLoading,
    error: subscriptionError,
  } = useQuery(GET_CURRENT_SUBSCRIPTION_PLAN, {
    fetchPolicy: "cache-and-network",
  });
  const {
    data: addressData,
    loading: addressLoading,
    refetch: refetchAddresses,
  } = useQuery(GET_ALL_ADDRESS, {
    fetchPolicy: "cache-and-network",
  });
  const stats = data?.getSellerProductStats;
  const currentPlan = subscriptionData?.getCurrentSubscriptionPlan?.plan;
  const subscription =
    subscriptionData?.getCurrentSubscriptionPlan?.subscription;
  const uploadedProducts = Number(stats?.totalProducts) || 0;
  const planLimit = getPlanLimit(currentPlan);
  const hasAddress = (addressData?.fetchAllAddresses?.length ?? 0) > 0;
  const addProductDisabled =
    !subscriptionError &&
    !subscriptionLoading &&
    planLimit !== Infinity &&
    uploadedProducts >= planLimit;

  const handleOpenAddProduct = () => {
    if (addProductDisabled || addressLoading) return;
    if (!hasAddress) {
      setRequireAddressOpen(true);
      return;
    }
    setAddProductOpen(true);
  };

  const handleCreateAddress = () => {
    setRequireAddressOpen(false);
    setAddAddressOpen(true);
  };

  const handleAddressCreated = async () => {
    setAddAddressOpen(false);
    await refetchAddresses?.();
    setAddProductOpen(true);
  };

  const statCards = STAT_CARD_DEFS.map(
    ({ field, format, label, icon, iconBg }) => ({
      label,
      icon,
      iconBg,
      value: loading ? "—" : format(stats?.[field]),
    }),
  );

  return (
    <div className="pb-10">
      <div className="flex items-center mt-10 justify-between mb-5">
        <div>
          <h1 className="text-[22px] font-semibold text-[#151515] my-0">
            My Products
          </h1>
          <p className="text-[13px] text-[#626262] my-0 mt-1">
            Manage your product catalog
          </p>
        </div>
        <div className="flex items-center gap-x-2 flex-col md:flex-row">
          <MaButton
            startIcon={<TfiDownload />}
            sx={{
              textTransform: "none",
              color: "#151515",
              borderColor: "#EAEAEA80",
              bgcolor: "#fff",

              "&:hover": {
                borderColor: "#D1D5DB",
                bgcolor: "#F9FAFB",
              },
            }}
          >
            Export
          </MaButton>
          <div className="hidden md:block">
            <MaButton
              variant={"primary"}
              startIcon={<MdAdd />}
              disabled={addProductDisabled}
              onClick={handleOpenAddProduct}
            >
              Add Product
            </MaButton>
          </div>
        </div>
      </div>
      <div className="block md:hidden">
        <MaButton
          fullWidth
          variant={"primary"}
          startIcon={<MdAdd />}
          disabled={addProductDisabled}
          onClick={handleOpenAddProduct}
        >
          Add Product
        </MaButton>
      </div>
      <SubscriptionLimitBanner
        plan={currentPlan}
        subscription={subscription}
        uploadedProducts={uploadedProducts}
        loading={subscriptionLoading}
        error={subscriptionError}
      />
      <div className="mt-5">
        <Grid container spacing={2}>
          <Grid item md={12} xs={12}>
            <div className="rounded-[12px] border border-[#EFEFEF] overflow-hidden grid grid-cols-2 sm:grid-cols-2 min-w-0">
              {statCards.map((card, idx) => (
                <StatCard key={idx} variant="grid" idx={idx} {...card} />
              ))}
            </div>
          </Grid>
          <Grid item md={12} xs={12}>
            <ProductTable
              onOpenAddProduct={handleOpenAddProduct}
              addProductDisabled={addProductDisabled}
            />
          </Grid>
        </Grid>
      </div>

      <AddProductModal
        isOpen={addProductOpen}
        onClose={() => setAddProductOpen(false)}
        onProductCreated={() => {
          client.refetchQueries({
            include: [GET_SELLER_PRODUCTS, GET_SELLER_PRODUCT_STATS],
          });
        }}
      />

      <RequireAddressDialog
        open={requireAddressOpen}
        onClose={() => setRequireAddressOpen(false)}
        onCreateAddress={handleCreateAddress}
      />

      <AddAddress
        hideTrigger
        open={addAddressOpen}
        onClose={() => setAddAddressOpen(false)}
        action={handleAddressCreated}
      />
    </div>
  );
};

export default SellerProducts;
